// src/components/pages/CreateGroup.js - Updated with Notifications
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronDown, ChevronUp, Users, User } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { API_BASE } from '../../utils/constants';

const CreateGroup = () => {
  const [formData, setFormData] = useState({
    groupName: '',
    selectedMembers: []
  });
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [errors, setErrors] = useState({});
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  // Load all users from database using existing endpoint
  const loadAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await fetch(`${API_BASE}/users`);
      
      if (!res.ok) {
        throw new Error(`Failed to fetch users: ${res.status}`);
      }
      
      const users = await res.json();
      console.log('Loaded all users:', users);
      setAllUsers(Array.isArray(users) ? users : []);
    } catch (err) {
      console.error('Failed to fetch users', err);
      try {
        const currentUserRes = await fetch(`${API_BASE}/users/email/${user.email}`);
        if (currentUserRes.ok) {
          const currentUser = await currentUserRes.json();
          const userResponse = {
            id: currentUser.id,
            name: currentUser.name,
            email: currentUser.email
          };
          setAllUsers([userResponse]);
        }
      } catch (fallbackErr) {
        console.error('Fallback also failed', fallbackErr);
        setAllUsers([]);
        if (window.showNotification) {
          window.showNotification('error', 'Failed to load users. Please refresh the page.');
        }
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadAllUsers();
    }
  }, [user]);

  // Validation function for group name
  const validateGroupName = (name) => {
    const errors = [];
    
    if (!name || name.trim().length === 0) {
      errors.push('Group name is required');
      return errors;
    }
    
    const trimmedName = name.trim();
    
    if (/^\d/.test(trimmedName)) {
      errors.push('Group name cannot start with a number');
    }
    
    if (!/[a-zA-Z]/.test(trimmedName)) {
      errors.push('Group name must contain at least one letter');
    }
    
    if (trimmedName.length < 2) {
      errors.push('Group name must be at least 2 characters long');
    }
    
    if (trimmedName.length > 50) {
      errors.push('Group name must be less than 50 characters');
    }
    
    return errors;
  };

  const handleGroupNameChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, groupName: value });
    
    const nameErrors = validateGroupName(value);
    setErrors({
      ...errors,
      groupName: nameErrors.length > 0 ? nameErrors : null
    });
  };

  // Filter users based on search term and exclude current user and already selected users
  const getFilteredUsers = () => {
    return allUsers.filter(u => {
      if (u.email === user.email) return false;
      if (formData.selectedMembers.find(member => member.id === u.id)) return false;
      
      if (searchTerm.trim() !== '') {
        const search = searchTerm.toLowerCase();
        return u.name.toLowerCase().includes(search) || 
               u.email.toLowerCase().includes(search);
      }
      
      return true;
    });
  };

  // Add user to selected members
  const addMember = (userToAdd) => {
    setFormData({
      ...formData,
      selectedMembers: [...formData.selectedMembers, userToAdd]
    });
    setSearchTerm('');
    if (window.showNotification) {
      window.showNotification('success', `Added ${userToAdd.name} to the group`);
    }
  };

  // Remove user from selected members
  const removeMember = (userToRemove) => {
    setFormData({
      ...formData,
      selectedMembers: formData.selectedMembers.filter(member => member.id !== userToRemove.id)
    });
    if (window.showNotification) {
      window.showNotification('info', `Removed ${userToRemove.name} from the group`);
    }
  };

  // Get current user info for display
  const getCurrentUserInfo = () => {
    const currentUserInList = allUsers.find(u => u.email === user.email);
    return currentUserInList || { 
      name: 'You', 
      email: user.email, 
      id: 'current' 
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const nameErrors = validateGroupName(formData.groupName);
    if (nameErrors.length > 0) {
      setErrors({ ...errors, groupName: nameErrors });
      return;
    }
    
    setLoading(true);
    setErrors({});

    try {
      const userRes = await fetch(`${API_BASE}/users/email/${user.email}`);
      if (!userRes.ok) {
        throw new Error('Failed to fetch user data');
      }
      const userData = await userRes.json();

      const memberUsernames = formData.selectedMembers.map(member => member.name);

      const payload = {
        name: formData.groupName.trim(),
        userId: userData.id,
        memberUsernames: memberUsernames
      };

      console.log('Creating group with payload:', payload);

      const createRes = await fetch(`${API_BASE}/groups/create-with-members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (createRes.ok) {
        const response = await createRes.text();
        if (window.showNotification) {
          window.showNotification('success', response);
        }
        navigate('/dashboard');
      } else {
        const errorText = await createRes.text();
        if (window.showNotification) {
          window.showNotification('error', errorText);
        }
      }
    } catch (err) {
      console.error('Error:', err);
      if (window.showNotification) {
        window.showNotification('error', 'Something went wrong. Please try again.');
      }
    }
    
    setLoading(false);
  };

  const filteredUsers = getFilteredUsers();
  const currentUserInfo = getCurrentUserInfo();

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow-sm rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Create a New Group</h1>
          <p className="mt-1 text-sm text-gray-600">
            Set up a new group and add members to start splitting expenses together.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
          {/* Group Name Field */}
          <div>
            <label htmlFor="groupName" className="block text-sm font-medium text-gray-700">
              Group Name *
            </label>
            <input
              type="text"
              id="groupName"
              required
              className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 ${
                errors.groupName ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., Goa Trip, Office Lunch, Roommate Expenses"
              value={formData.groupName}
              onChange={handleGroupNameChange}
            />
            {errors.groupName && (
              <div className="mt-2">
                {errors.groupName.map((error, index) => (
                  <p key={index} className="text-sm text-red-600 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {error}
                  </p>
                ))}
              </div>
            )}
            <div className="mt-2 text-xs text-gray-500">
              <p>Rules: Must contain at least one letter, cannot start with a number, 2-50 characters</p>
            </div>
          </div>

          {/* Members Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Group Members
            </label>
            
            {/* Current User (Always Included) */}
            <div className="mb-3 p-3 bg-teal-50 border border-teal-200 rounded-md">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center mr-3">
                  <User className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-teal-900">{currentUserInfo.name}</p>
                  <p className="text-xs text-teal-700">{currentUserInfo.email} • Group Admin</p>
                </div>
              </div>
            </div>

            {/* Selected Members */}
            {formData.selectedMembers.length > 0 && (
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-2">Selected Members:</p>
                <div className="space-y-2">
                  {formData.selectedMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-2 bg-gray-50 border rounded-md">
                      <div className="flex items-center">
                        <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center mr-2">
                          <User className="h-3 w-3 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{member.name}</p>
                          <p className="text-xs text-gray-500">{member.email}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeMember(member)}
                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                        title="Remove member"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Members Dropdown */}
            <div className="relative">
              <div
                className={`border rounded-md p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors ${
                  showDropdown ? 'border-teal-500 ring-2 ring-teal-200' : 'border-gray-300'
                }`}
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-gray-500" />
                    {loadingUsers ? 'Loading users...' : 'Add members to group'}
                  </span>
                  {showDropdown ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </div>
              </div>

              {showDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-64 overflow-hidden">
                  {/* Search Box */}
                  <div className="p-3 border-b border-gray-100 bg-gray-50">
                    <input
                      type="text"
                      placeholder="Search users by name or email..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Users List */}
                  <div className="max-h-48 overflow-y-auto">
                    {loadingUsers ? (
                      <div className="p-6 text-center text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-2"></div>
                        <p className="text-sm">Loading users...</p>
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <Users className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm">
                          {searchTerm.trim() ? 'No users found matching your search' : 'No more users available to add'}
                        </p>
                        {searchTerm.trim() && (
                          <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="mt-2 text-xs text-teal-600 hover:text-teal-700"
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {filteredUsers.map((userItem) => (
                          <div
                            key={userItem.id}
                            className="p-3 hover:bg-teal-50 cursor-pointer transition-colors group"
                            onClick={() => addMember(userItem)}
                          >
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-r from-teal-400 to-blue-400 rounded-full flex items-center justify-center mr-3">
                                <User className="h-4 w-4 text-white" />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{userItem.name}</p>
                                <p className="text-xs text-gray-500">{userItem.email}</p>
                              </div>
                              <div className="text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                <span className="text-xs">Click to add</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <p className="mt-2 text-sm text-gray-500">
              <span className="font-medium">Total members:</span> {formData.selectedMembers.length + 1} 
              (including you)
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading || 
                (errors.groupName && errors.groupName.length > 0) ||
                !formData.groupName.trim() ||
                loadingUsers
              }
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                'Create Group'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroup;